import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, CheckList, Dropdown, DropdownRef, Empty, SearchBar, SearchBarRef, Tabs } from 'antd-mobile';
import styles from './index.module.scss';
import {
  getAdminPageList,
  AdminPageResponse,
  getStaffDept,
  GetStaffDeptPostResponse,
  GetAreaListPostResponse,
  EnergyConsumptionAdminList,
  getEnergyConsumptionAdminList,
  AdminPageRequest,
  getAreaList2,
} from '@/api/energy';
import { isEmpty } from 'lodash-es';
import NoData from '@/assets/images/noData.png';
import dayjs from 'dayjs';
import { getWeekRange } from '../EnergyUsePlan/util';
import DateRange from './components/DateRange';
import List from './components/list';
import Detail from './Detail';

const EnergyUseManagement = () => {
  const [data, setData] = useState<AdminPageResponse[]>([]);
  const [sectionOp, setSectionOp] = useState<GetStaffDeptPostResponse[]>([]);
  const [areaOp, setAreaOp] = useState<GetAreaListPostResponse[]>([]);
  const [selectedDept, setSelectedDept] = useState<number[]>();
  const [selectedArea, setSelectedArea] = useState<number[]>();
  const [searchParams, setSearchParams] = useState<AdminPageRequest>({ page: 1, size: 20 });
  const [hasMore, setHasMore] = useState(false);

  const [activeTab, setActiveTab] = useState<string>();
  const [mediumList, setMediumList] = useState<EnergyConsumptionAdminList[]>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs | null]>();
  const [visible, setVisible] = useState<boolean>(false);
  const [editNodeData, setEditNodeData] = useState<any>(undefined);

  const [searchDeptText, setSearchDeptText] = useState('');
  const [searchAreaText, setSearchAreaText] = useState('');

  const ref = useRef<DropdownRef>(null);
  const searchOwnerRef = useRef<SearchBarRef>(null);
  const searchZoneRef = useRef<SearchBarRef>(null);

  useEffect(() => {
    document.title = '用能管理';
  }, []);

  useEffect(() => {
    getStaffDept().then(res => {
      setSectionOp(res.list);
    });
  }, []);

  useEffect(() => {
    if (!isEmpty(searchParams?.ownerOrgId)) {
      getAreaList2(searchParams?.ownerOrgId!).then(res => {
        setAreaOp(res.list);
      });
    } else {
      setAreaOp([]);
    }
    setSelectedArea(undefined);
  }, [searchParams.ownerOrgId]);

  useEffect(() => {
    // 默认选择本周期
    const [start, end] = getWeekRange(dayjs());
    setDateRange([start, end]);
  }, []);

  useEffect(() => {
    getEnergyConsumptionAdminList().then(res => {
      setMediumList(res.list ?? []);
      if (res.list) {
        setActiveTab(String(res.list[0].energyMediumId || 0));
      }
    });
  }, []);

  useEffect(() => {
    getAdminPageList({
      ...searchParams,
      startDate: dateRange ? dateRange[0]?.valueOf() : undefined,
      endDate: dateRange ? dateRange[1]?.valueOf() : undefined,
      energyMediumId: Number(activeTab),
    }).then(res => {
      if (searchParams.page === 1) {
        setData(res.list ?? []);
      } else {
        setData(prev => [...prev, ...(res.list ?? [])]);
      }
      setHasMore(res.list?.length > 0 && res.total > 20);
    });
  }, [searchParams]);

  useEffect(() => {
    setSearchParams(prev => ({ ...prev, page: 1 }));
  }, [dateRange, activeTab]);

  const filteredDeptItems = useMemo(() => {
    if (searchDeptText) {
      return sectionOp.filter(item => item.ownerOrgName!.includes(searchDeptText));
    } else {
      return sectionOp;
    }
  }, [sectionOp, searchDeptText]);
  const filteredAreaItems = useMemo(() => {
    return areaOp.filter(item => item.zoneName!.includes(searchAreaText));
  }, [areaOp, searchAreaText]);

  const loadMore = async () => {
    const nextPage = searchParams.page! + 1;
    setSearchParams(prev => ({ ...prev, page: nextPage }));
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Dropdown
          ref={ref}
          onChange={key => {
            if (key) {
              setSelectedDept(searchParams.ownerOrgId);
              setSelectedArea(searchParams.areaId);
            } else {
              setSearchAreaText('');
              setSearchDeptText('');
              searchOwnerRef.current?.clear();
              searchZoneRef.current?.clear();
            }
          }}
        >
          <Dropdown.Item
            key="ownerOrgId"
            title="部门"
            style={{ color: isEmpty(searchParams!.ownerOrgId) ? '' : 'var(--adm-color-primary)' }}
          >
            <div className={styles.dropdownPanel}>
              <SearchBar placeholder="请输入部门名称" ref={searchOwnerRef} onChange={setSearchDeptText} />
              {!isEmpty(filteredDeptItems) ? (
                <CheckList
                  multiple
                  value={selectedDept ?? []}
                  onChange={val => {
                    setSelectedDept(val as number[]);
                  }}
                >
                  {filteredDeptItems?.map(item => (
                    <CheckList.Item key={item.ownerOrgId} value={item.ownerOrgId!}>
                      {item.ownerOrgName}
                    </CheckList.Item>
                  ))}
                </CheckList>
              ) : (
                <div className={styles.emptyState}>
                  <Empty image={NoData} description="暂无数据" />
                </div>
              )}
              <div className={styles.actionBar}>
                <Button size="large" onClick={() => setSelectedDept(undefined)}>
                  重置
                </Button>
                <Button
                  size="large"
                  color="primary"
                  fill="solid"
                  onClick={() => {
                    setSearchParams(prev => ({ ...prev, page: 1, ownerOrgId: selectedDept, areaId: undefined }));
                    ref.current?.close();
                  }}
                >
                  确定
                </Button>
              </div>
            </div>
          </Dropdown.Item>
          <Dropdown.Item
            key="zoneIdList"
            title="区域名称"
            style={{ color: isEmpty(searchParams!.areaId) ? '' : 'var(--adm-color-primary)' }}
          >
            <div className={styles.dropdownPanel}>
              <SearchBar placeholder="请输入区域名称" ref={searchZoneRef} onChange={setSearchAreaText} />
              {!isEmpty(filteredAreaItems) ? (
                <CheckList
                  multiple
                  value={selectedArea ?? []}
                  onChange={val => {
                    setSelectedArea(val as number[]);
                  }}
                >
                  {filteredAreaItems?.map(item => (
                    <CheckList.Item key={item.id} value={item.id!}>
                      {item.zoneName}
                    </CheckList.Item>
                  ))}
                </CheckList>
              ) : (
                <div className={styles.emptyState}>
                  <Empty
                    image={NoData}
                    description={searchParams?.ownerOrgId ? '暂无数据' : '暂无数据，请先选择部门'}
                  />
                </div>
              )}
              <div className={styles.actionBar}>
                <Button size="large" onClick={() => setSelectedArea(undefined)}>
                  重置
                </Button>
                <Button
                  size="large"
                  color="primary"
                  fill="solid"
                  onClick={() => {
                    setSearchParams(prev => ({ ...prev, page: 1, areaId: selectedArea }));
                    ref.current?.close();
                  }}
                >
                  确定
                </Button>
              </div>
            </div>
          </Dropdown.Item>
        </Dropdown>
        <DateRange dateRange={dateRange} setDateRange={setDateRange} />
        <Tabs activeKey={activeTab} onChange={setActiveTab} className="tabs">
          {mediumList?.map(medium => (
            <Tabs.Tab title={medium.energyMediumName} key={String(medium.energyMediumId)} />
          ))}
        </Tabs>
      </div>
      <List
        data={data}
        hasMore={hasMore}
        loadMore={loadMore}
        activeTab={activeTab}
        dateRange={dateRange}
        onClickDetail={params => {
          setVisible(true);
          setEditNodeData(params);
        }}
      />
      {visible && (
        <Detail onCancel={() => setVisible(false)} params={editNodeData} energyMediumId={Number(activeTab)} />
      )}
    </div>
  );
};

export default EnergyUseManagement;
